import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
    const { login, register } = useAuth();
    const { t } = useLanguage();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/";
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (mode === "register" && !acceptedTerms) {
            toast.error(t("Debes aceptar los Términos y Condiciones para crear tu cuenta", "You must accept the Terms & Conditions to create your account"));
            return;
        }
        setLoading(true);
        try {
            if (mode === "login") await login(email, password);
            else await register(name, email, password, acceptedTerms);
            toast.success(t("Bienvenido a Terra Boutique", "Welcome to Terra Boutique"));
            nav(next);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const sendReset = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        try {
            await api.post("/auth/password-reset-request", { email: forgotEmail });
            setForgotSent(true);
        } catch (err) {
            toast.error(t("No se pudo enviar el enlace. Intenta de nuevo.", "Could not send the link. Please try again."));
        } finally { setForgotLoading(false); }
    };

    const googleAuth = () => {
        const redirect = encodeURIComponent(window.location.origin + "/");
        window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center px-6 py-12 fade-in">
            <div className="tb-card p-10 w-full max-w-md">
                <div className="tb-overline text-[var(--tb-gold)] mb-2">Terra Boutique</div>
                <h1 className="font-display text-3xl mb-6">{mode === "login" ? t("Iniciar sesión", "Sign in") : t("Crear cuenta", "Create account")}</h1>
                <button onClick={googleAuth} className="tb-btn-secondary w-full mb-6 flex items-center justify-center gap-3" data-testid="google-login-button">
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#E5BE5A" d="M21.35 11.1h-9.17v2.99h5.25c-.23 1.43-1.65 4.2-5.25 4.2-3.16 0-5.74-2.61-5.74-5.84 0-3.23 2.58-5.84 5.74-5.84 1.8 0 3.01.77 3.7 1.43l2.53-2.43C16.78 4.18 14.66 3.2 12.18 3.2 6.99 3.2 2.78 7.41 2.78 12.6s4.21 9.4 9.4 9.4c5.43 0 9.03-3.81 9.03-9.18 0-.62-.07-1.09-.16-1.62z" /></svg>
                    {t("Continuar con Google", "Continue with Google")}
                </button>
                <div className="text-center text-xs text-[var(--tb-muted)] mb-4">— {t("o", "or")} —</div>
                <form onSubmit={submit} className="space-y-4">
                    {mode === "register" && (
                        <div>
                            <label className="tb-overline block mb-1">{t("Nombre", "Name")}</label>
                            <input className="tb-input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" />
                        </div>
                    )}
                    <div>
                        <label className="tb-overline block mb-1">{t("Correo", "Email")}</label>
                        <input type="email" className="tb-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid={`${mode}-email`} />
                    </div>
                    <div>
                        <label className="tb-overline block mb-1">{t("Contraseña", "Password")}</label>
                        <input type="password" className="tb-input" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid={`${mode}-password`} />
                    </div>
                    {mode === "login" && (
                        <div className="text-right">
                            <button type="button" onClick={() => { setShowForgot(true); setForgotSent(false); setForgotEmail(email); }} className="text-xs text-[var(--tb-gold)] hover:underline" data-testid="forgot-link">
                                {t("¿Olvidaste tu contraseña?", "Forgot your password?")}
                            </button>
                        </div>
                    )}
                    {mode === "register" && (
                        <label className="flex items-start gap-2 cursor-pointer text-xs text-[var(--tb-text-2)]" data-testid="terms-checkbox-label">
                            <input type="checkbox" checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="mt-0.5 accent-[var(--tb-gold)]"
                                data-testid="terms-checkbox" />
                            <span>
                                {t("He leído y acepto los", "I have read and accept the")} <Link to="/legal/terminos" target="_blank" className="text-[var(--tb-gold)] hover:underline">{t("Términos y Condiciones", "Terms & Conditions")}</Link> {t("y la", "and the")} <Link to="/legal/privacidad" target="_blank" className="text-[var(--tb-gold)] hover:underline">{t("Política de Privacidad", "Privacy Policy")}</Link>.
                            </span>
                        </label>
                    )}
                    <button type="submit" disabled={loading || (mode === "register" && !acceptedTerms)} className="tb-btn-primary w-full disabled:opacity-50" data-testid={`${mode}-submit`}>
                        {loading ? t("Cargando...", "Loading...") : (mode === "login" ? t("Acceder", "Sign in") : t("Registrarse", "Register"))}
                    </button>
                </form>
                <div className="text-center mt-6 text-sm">
                    {mode === "login" ? (
                        <Link to="/registro" className="tb-link">{t("¿Sin cuenta? Regístrate", "No account? Register")}</Link>
                    ) : (
                        <Link to="/login" className="tb-link">{t("¿Ya tienes cuenta? Accede", "Already have an account? Sign in")}</Link>
                    )}
                </div>
            </div>

            {showForgot && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto fade-in" onClick={() => setShowForgot(false)}>
                    <div className="tb-card p-8 w-full max-w-md my-4" onClick={(e) => e.stopPropagation()} data-testid="forgot-modal">
                        {!forgotSent ? (
                            <>
                                <h2 className="font-display text-2xl mb-2">{t("Recuperar contraseña", "Recover password")}</h2>
                                <p className="text-sm text-[var(--tb-text-2)] mb-5">{t("Ingresa tu correo y te enviaremos un enlace para crear una nueva contraseña. El enlace expira en 1 hora.", "Enter your email and we'll send you a link to create a new password. The link expires in 1 hour.")}</p>
                                <form onSubmit={sendReset} className="space-y-3">
                                    <input type="email" required className="tb-input" placeholder={t("tu@correo.com", "you@email.com")} value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} data-testid="forgot-email" autoFocus />
                                    <button type="submit" disabled={forgotLoading} className="tb-btn-primary w-full disabled:opacity-50" data-testid="forgot-submit">
                                        {forgotLoading ? t("Enviando...", "Sending...") : t("Enviar enlace", "Send link")}
                                    </button>
                                    <button type="button" onClick={() => setShowForgot(false)} className="tb-link text-xs w-full text-center">{t("Cancelar", "Cancel")}</button>
                                </form>
                            </>
                        ) : (
                            <>
                                <h2 className="font-display text-2xl mb-3">📧 {t("Revisa tu correo", "Check your email")}</h2>
                                <p className="text-sm text-[var(--tb-text-2)] mb-4">
                                    {t("Si", "If")} <span className="font-mono text-[var(--tb-text)]">{forgotEmail}</span> {t("está registrado, recibirás un enlace en unos minutos.", "is registered, you'll receive a link in a few minutes.")}
                                </p>
                                <p className="text-xs text-[var(--tb-muted)] mb-5">
                                    {t("¿No lo ves? Revisa tu carpeta de spam o solicita uno nuevo.", "Can't find it? Check your spam folder or request a new one.")}
                                </p>
                                <button onClick={() => setShowForgot(false)} className="tb-btn-secondary w-full" data-testid="forgot-close">{t("Entendido", "Got it")}</button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
